import { useState, useRef, useEffect, useCallback } from 'react';
import useCopilotTracking from '@components/copilot/useCopilotTracking';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setSidebarOpen, selectSidebarCollapsed } from '@store/uiSlice';
import { useAuth } from '@hooks/useAuth';
import { useRealtimeNotification } from '@hooks/useRealtime';
import { Avatar, SearchBar } from '@components/ui';
import {
  useHlAiClusterNotificationsMutation,
  useHlListNotificationsQuery, useHlMarkNotificationReadMutation, useHlMarkAllNotificationsReadMutation,
} from '@api/hiveLinkApi';
import {
  Menu,
  Bell,
  ChevronRight,
  User,
  Settings,
  LogOut,
  HelpCircle,
  KeyRound,
  CreditCard,
  Palette,
  Moon,
  Sun,
  Check,
  CheckCheck,
  Hash,
  AtSign,
  MessageCircle,
  AlarmClock,
  ShieldAlert,
} from 'lucide-react';

// ── Breadcrumb labels for routes ──
const ROUTE_LABELS = {
  '/': 'Dashboard',
  '/projects': 'Projects',
  '/tasks': 'Tasks',
  '/sprints': 'Sprints',
  '/kanban': 'Kanban Board',
  '/gantt': 'Gantt Chart',
  '/milestones': 'Milestones',
  '/calendar': 'Calendar',
  '/hivelink': 'HiveLink',
  '/timesheets': 'Timesheets',
  '/budget': 'Budget',
  '/goals': 'Goals',
  '/reports': 'Reports',
  '/documents': 'Documents',
  '/knowledge-base': 'Knowledge Base',
  '/workflows': 'Workflows',
  '/members': 'Team Members',
  '/settings': 'Settings',
  '/capacity': 'CapacityIQ',
  '/zuno': 'Zuno AI',
  '/audit-log': 'Audit Log',
  '/admin': 'Admin Panel',
  '/client-manage': 'Client Portal',
  '/download': 'Desktop App',
  '/ui': 'Components',
};

const Breadcrumbs = () => {
  const { pathname } = useLocation();
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) {
    return <span className="text-sm font-semibold text-surface-800">Dashboard</span>;
  }

  const crumbs = segments.map((seg, i) => {
    const path = '/' + segments.slice(0, i + 1).join('/');
    const label = ROUTE_LABELS[path] || seg.charAt(0).toUpperCase() + seg.slice(1);
    const isLast = i === segments.length - 1;
    return { path, label, isLast };
  });

  return (
    <nav className="flex items-center gap-1.5 text-sm">
      <Link to="/" className="text-surface-500 hover:text-surface-600 transition-colors">
        Dashboard
      </Link>
      {crumbs.map((crumb) => (
        <span key={crumb.path} className="flex items-center gap-1.5">
          <ChevronRight size={12} className="text-surface-300" />
          {crumb.isLast ? (
            <span className="font-semibold text-surface-800">{crumb.label}</span>
          ) : (
            <Link to={crumb.path} className="text-surface-500 hover:text-surface-600 transition-colors">
              {crumb.label}
            </Link>
          )}
        </span>
      ))}
    </nav>
  );
};

// ── Notification Bell ──
// Notification sound via AudioContext (no external dependency)

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const { data, refetch } = useHlListNotificationsQuery({});
  const [markRead] = useHlMarkNotificationReadMutation();
  const [markAllRead] = useHlMarkAllNotificationsReadMutation();

  const notifications = data?.notifications || [];
  const unreadCount = data?.unreadCount || 0;

  /**
   * Notification clustering.
   *
   * /ai/cluster-notifications existed and nothing called it. I first assumed it
   * belonged after the send loop, which was wrong — it reads the recipient's own
   * unread notifications and groups them, so it can only run at read time. You
   * cannot group a notification with ones that do not exist yet.
   *
   * Opt-in per open rather than automatic: it is an LLM call, and most people
   * with four notifications do not need them summarised.
   */
  const [clusterApi, { isLoading: clustering }] = useHlAiClusterNotificationsMutation();
  const [clusters, setClusters] = useState(null);
  const [clusterError, setClusterError] = useState(null);

  const groupThem = async () => {
    setClusterError(null);
    try {
      const res = await clusterApi({}).unwrap();
      setClusters(res?.clusters || res?.data?.clusters || []);
    } catch (err) {
      setClusterError(err?.data?.message || 'Could not group them');
    }
  };

  // A fresh list invalidates the grouping it was built from.
  useEffect(() => { setClusters(null); }, [data]);

  // Play notification sound (AudioContext — no external files)
  const playSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      [523.25, 659.25].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = freq; osc.type = 'sine';
        gain.gain.setValueAtTime(0.15, ctx.currentTime + i * 0.12);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.12 + 0.25);
        osc.start(ctx.currentTime + i * 0.12);
        osc.stop(ctx.currentTime + i * 0.12 + 0.25);
      });
    } catch {}
  }, []);

  // Show browser desktop notification
  const showDesktopNotif = useCallback((n) => {
    try {
      if (Notification.permission === 'granted') {
        const notif = new Notification(n.title || 'HiveLink', {
          body: n.message || '',
          icon: '/favicon.ico',
          tag: n._id || 'hivelink',
          silent: true, // we handle sound separately
        });
        notif.onclick = () => {
          window.focus();
          if (n.metadata?.channelId) navigate(`/hivelink/${n.metadata.channelId}`);
          notif.close();
        };
        setTimeout(() => notif.close(), 6000);
      } else if (Notification.permission === 'default') {
        Notification.requestPermission();
      }
    } catch {}
  }, [navigate]);

  // Real-time listener — handle desktop + sound
  const handleNewNotif = useCallback((n) => {
    refetch();
    if (n?._prefs?.sound !== false) playSound();
    if (n?._prefs?.desktop !== false) showDesktopNotif(n);
  }, [refetch, playSound, showDesktopNotif]);
  useRealtimeNotification(handleNewNotif);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fmtTime = (d) => {
    const diff = (Date.now() - new Date(d).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    return `${Math.floor(diff/86400)}d ago`;
  };

  const getIcon = (type) => {
    if (type === 'hivelink_mention') return <AtSign size={14} className="text-blue-500"/>;
    if (type === 'hivelink_keyword') return <Hash size={14} className="text-amber-500"/>;
    if (type === 'hivelink_reminder') return <AlarmClock size={14} style={{ color: "#1338be" }}/>;
    if (type === 'hivelink_toxicity') return <ShieldAlert size={14} className="text-red-500"/>;
    return <MessageCircle size={14} className="text-primary-500"/>;
  };

  const handleClick = async (n) => {
    if (!n.isRead) await markRead(n._id).catch(()=>{});
    if (n.metadata?.channelId) navigate(`/hivelink/${n.metadata.channelId}`);
    setOpen(false);
  };

  const handleMarkAllRead = async () => {
    await markAllRead().catch(()=>{});
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen(!open); if (!open) refetch(); }}
        className="relative p-2 text-surface-600 hover:text-surface-700 hover:bg-surface-100 rounded-xl transition-colors"
        aria-label="Notifications"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 flex items-center justify-center bg-danger-500 text-white text-[11px] font-bold rounded-full px-1 ring-2 ring-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white dark:bg-surface-900 rounded-2xl shadow-dropdown border border-surface-200 dark:border-surface-700 z-50 animate-fade-in-down overflow-hidden">
          <div className="px-4 py-3 border-b border-surface-100 dark:border-surface-800 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-surface-900 dark:text-surface-100">Notifications {unreadCount > 0 && <span className="ml-1 text-xs font-normal text-primary-500">({unreadCount} new)</span>}</h3>
            {unreadCount > 0 && (
              <button onClick={handleMarkAllRead} className="text-xs text-primary-500 hover:text-primary-700 flex items-center gap-1"><CheckCheck size={13}/>Mark all read</button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-10 text-center">
                <Bell size={28} className="mx-auto mb-2 text-surface-300" />
                <p className="text-sm text-surface-600">No notifications yet</p>
                <p className="text-xs text-surface-500 mt-0.5">Keyword alerts, mentions & reminders will appear here</p>
              </div>
            ) : (
              notifications.map(n => (
                <button key={n._id} onClick={() => handleClick(n)}
                  className={`w-full text-left px-4 py-3 border-b border-surface-50 dark:border-surface-800 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors flex gap-3 items-start
                    ${!n.isRead ? 'bg-primary-50/40 dark:bg-primary-900/10' : ''}`}>
                  <div className="mt-0.5 p-1.5 rounded-lg bg-surface-100 dark:bg-surface-700 shrink-0">{getIcon(n.type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-xs font-medium truncate ${!n.isRead ? 'text-surface-900 dark:text-surface-100' : 'text-surface-600 dark:text-surface-500'}`}>{n.title}</p>
                      {!n.isRead && <div className="w-2 h-2 rounded-full bg-primary-500 shrink-0"/>}
                    </div>
                    <p className="text-xs text-surface-600 mt-0.5 line-clamp-1">{n.message}</p>
                    <p className="text-[11px] text-surface-500 mt-1">{fmtTime(n.createdAt)}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── User Avatar Dropdown ──
const UserMenu = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const menuItems = [
    { icon: User, label: 'Profile', action: () => navigate('/settings') },
    { icon: Settings, label: 'Settings', action: () => navigate('/settings') },
    { icon: KeyRound, label: 'Security', action: () => navigate('/settings') },
    // SC4 — Billing promoted to top-level; previously only reachable via Settings → Billing tab
    { icon: CreditCard, label: 'Billing & Plan', action: () => navigate('/billing') },
    'divider',
    { icon: HelpCircle, label: 'Help & Docs', action: () => copilotTrack.helpOpen({ from: 'topbar' }) },
    'divider',
    { icon: LogOut, label: 'Sign out', action: logout, danger: true },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        aria-label="Open user menu"
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2.5 px-2 py-1.5 rounded-xl hover:bg-surface-100 transition-colors"
      >
        <Avatar
          src={user?.avatar}
          name={user?.name || 'User'}
          size="sm"
          status="online"
        />
        <div className="hidden md:block text-left">
          <p className="text-sm font-semibold text-surface-800 leading-tight max-w-[120px] truncate">
            {user?.name}
          </p>
          <p className="text-2xs text-surface-600 leading-tight">
            {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
          </p>
        </div>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-dropdown border border-surface-200 py-1.5 z-50 animate-fade-in-down">
          {/* User info header */}
          <div className="px-4 py-3 border-b border-surface-100">
            <p className="text-sm font-semibold text-surface-900 truncate">{user?.name}</p>
            <p className="text-xs text-surface-600 truncate">{user?.email}</p>
          </div>

          {/* Menu items */}
          <div className="py-1">
            {menuItems.map((item, i) => {
              if (item === 'divider') return <hr key={i} className="my-1 border-surface-100" />;
              return (
                <button
                  key={item.label}
                  onClick={() => { item.action(); setOpen(false); }}
                  className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${
                    item.danger
                      ? 'text-danger-600 hover:bg-danger-50'
                      : 'text-surface-700 hover:bg-surface-50'
                  }`}
                >
                  <item.icon size={15} className="flex-shrink-0" />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════
//  TOPBAR
// ═══════════════════════════════════════════
const Topbar = () => {
  const copilotTrack = useCopilotTracking();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const collapsed = useSelector(selectSidebarCollapsed);
  const [searchQuery, setSearchQuery] = useState('');

  // Navigate to the unified global search results page.
  const handleSearch = (query) => {
    const q = (query || '').trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-lg border-b border-surface-200/80">
      <div className="flex items-center justify-between h-14 px-4 lg:px-6">
        {/* Left: mobile hamburger + breadcrumbs */}
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => dispatch(setSidebarOpen(true))}
            className="lg:hidden p-2 text-surface-600 hover:text-surface-700 hover:bg-surface-100 rounded-xl transition-colors"
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
          <Breadcrumbs />
        </div>

        {/* Right: search + notifications + user */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            onSearch={handleSearch}
            placeholder="Search anything..."
            size="sm"
            showShortcut
            expandable
            containerClassName="hidden sm:block"
          />
          <NotificationBell />
          <UserMenu />
        </div>
      </div>
    </header>
  );
};

export default Topbar;